import React, { useEffect, useRef } from "react";
import { UpdateAggregationPanel, CSAggrData, ColumnProportionEm } from "../../common/communication.base";
import { NclAggregationPanel } from "../../common/components.ncl";
import { WithContextPlacementProps } from "../k2hoc";
import K2Img from "../Image/K2Img";
import K2TruncateText from "../Text/K2TruncateText";
import K2ColGroup from "./K2ColGroup";
import { ColumnsProps, FixedColumnOffset, getFixedColumnStyles, getTextAlign } from "./utils";
import css from "./DataGrid.scss";
import { useServerState } from "../hooks";
import resolveContextMenu from "../../utils/resolveContextMenu";

interface AggregationPanelProps extends WithContextPlacementProps, ColumnsProps {
  width: number;
  scrollLeft: number;
  getFixedColumnOffset: FixedColumnOffset;
}

const AggregationPanel = (props: AggregationPanelProps) => {
  const [control, data, element] = useServerState<NclAggregationPanel, UpdateAggregationPanel, HTMLDivElement>(
    props.controlUID,
    props.vrUID,
    (ctrl) => ctrl instanceof NclAggregationPanel
  );
  useEffect(() => {
    if (!element.current || data.Visible !== true) return;

    element.current.scrollLeft = props.scrollLeft;
  }, [element, data.Visible, props.scrollLeft]);

  if (!data.Data || data.Visible !== true) return null;

  const values: Array<CSAggrData> = data.Data.toJS() as CSAggrData[];
  return (
    <div ref={element} className={`${css.dg_aggregation_wrapper}${data.InvalidData ? ` ${css.dg_invalid_data}` : ""}`}>
      <table tabIndex={1} className={css.dg_aggregation} style={{ width: props.width + "px" }}>
        {props.columnsProportion && (
          <K2ColGroup
            columnsProportion={props.columnsProportion}
            fixedColumnsCount={props.fixedColumnsCount}
            columnsWidths={props.columnsWidths}
            columnsVersion={props.columnsVersion}
          />
        )}
        <tbody>
          <tr>
            {values.map((value: CSAggrData, index: number) => {
              if (props.columnsProportion && props.columnsProportion.length > index) {
                const proportion = props.columnsProportion[index];

                return <AggregationItem key={"column_aggr_" + index} index={index} proportion={proportion} value={value} control={control} {...props} />;
              }
            })}
          </tr>
        </tbody>
      </table>
    </div>
  );
};

interface AggregationItemProps extends AggregationPanelProps {
  index: number;
  value: CSAggrData;
  proportion: ColumnProportionEm;
  control: NclAggregationPanel;
}

const AggregationItem = (props: AggregationItemProps) => {
  const element = useRef<HTMLTableHeaderCellElement>(null);

  const handleContextMenu = (e: MouseEvent | TouchEvent | Event, index: number) => {
    console.log("aggregation");
    props.control.contextMenu(index);
    e.preventDefault();
  };

  useEffect(() => {
    resolveContextMenu(element.current, (e) => handleContextMenu(e, props.index));
  }, []);

  return (
    <th
      ref={element}
      className={css.dg_aggregation_column + `${props.index < props.fixedColumnsCount ? " dg_fixed_col" : ""}`}
      style={getFixedColumnStyles(props.proportion.Width, props.getFixedColumnOffset(props.columnsWidths, props.index, props.index < props.fixedColumnsCount))}
    >
      <div className={css.dg_aggregation_cell}>
        {props.value.GlyphId && (
          <K2Img
            glyphId={props.value.GlyphId}
            vcx={props.control.VCX}
            height={props.control.VCX.sizeMap(16)}
            width={props.control.VCX.sizeMap(16)}
            strokeColor={props.control.VCX.getColor(props.control.VCX.Data.ColorMap.BaseColorBck1)}
          />
        )}
        <K2TruncateText style={{ width: "100%", textAlign: getTextAlign(props.value.Align), alignSelf: "center", fontWeight: "normal" }}>
          {props.value.Text.toString()}
        </K2TruncateText>
      </div>
    </th>
  );
};

export default AggregationPanel;
